
import { Helmet } from "react-helmet";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Helmet>
        <title>Privacy Policy - Feednatic</title>
        <meta
          name="description"
          content="Privacy Policy for Feednatic - Learn how we collect, use, and protect your information."
        />
      </Helmet>

      {/* Header/Navigation */}
      <header className="border-b border-white/10 bg-[#0a0a0a]/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img src="lovable-uploads/48a2ed1e-15ea-4dbf-bdbc-f70f710ca82a.png" alt="Logo" className="h-8" />
            <span className="font-bold text-xl hidden sm:inline-block bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
              Feednatic
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" className="text-white hover:text-white hover:bg-white/10" onClick={() => navigate("/blog")}>
              Blog
            </Button>
            <Button variant="ghost" className="text-white hover:text-white hover:bg-white/10" onClick={() => navigate("/auth")}>
              Log in
            </Button>
            <Button className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white" onClick={() => navigate("/auth")}>Sign up</Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <Button variant="ghost" className="mb-6 flex items-center gap-2 text-white hover:text-white hover:bg-white/10" onClick={() => navigate("/")}>
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Button>

        <h1 className="text-5xl font-black mb-6 bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">Privacy Policy</h1>
        <p className="text-white/60 mb-4">Last updated: May 20, 2025</p>
        
        <div className="space-y-8">
          <section className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-4 text-white">1. Introduction</h2>
            <p className="text-white/70">
              Feednatic ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our feedback management platform ("Platform").
            </p>
          </section>

          <section className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-4 text-white">2. Information We Collect</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold mb-2 text-white">2.1 Personal Information</h3>
                <p className="text-white/70">
                  We may collect personal information that you voluntarily provide to us when you register for an account, such as your name, email address, and company name. If you choose to subscribe to our newsletter or contact us, we may also collect additional information you provide.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold mb-2 text-white">2.2 Usage Data</h3>
                <p className="text-white/70">
                  We automatically collect certain information when you visit, use, or navigate our Platform. This information may include your IP address, browser type, operating system, referring webpages, pages visited, and the dates/times you accessed the Platform. This information is primarily needed to maintain the security and operation of our Platform.
                </p>
              </div>
            </div>
          </section>

          <section className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-4 text-white">3. How We Use Your Information</h2>
            <p className="text-white/70">We use the information we collect or receive:</p>
            <ul className="list-disc list-inside mt-2 space-y-2 text-white/70">
              <li>To facilitate account creation and the login process</li>
              <li>To provide and maintain our Platform's functionality and features</li>
              <li>To respond to your inquiries and provide customer support</li>
              <li>To send you administrative information, such as updates, security alerts, and support messages</li>
              <li>To personalize your experience and deliver content relevant to your interests</li>
              <li>To improve our Platform and develop new features</li>
              <li>To prevent fraud and abuse of our Platform</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          <section className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-4 text-white">4. Disclosure of Your Information</h2>
            <p className="text-white/70">
              We may share information we have collected in certain situations. Your information may be disclosed as follows:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-2 text-white/70">
              <li>With service providers who perform services for us or on our behalf</li>
              <li>To comply with our legal obligations</li>
              <li>To protect the rights and safety of our users and third parties</li>
              <li>In connection with a business transaction such as a merger, acquisition, or asset sale</li>
            </ul>
          </section>

          <section className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-4 text-white">5. Your Privacy Rights</h2>
            <p className="text-white/70">
              Depending on your location, you may have certain rights regarding your personal information, including:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-2 text-white/70">
              <li>The right to access personal information we hold about you</li>
              <li>The right to request correction of your personal information</li>
              <li>The right to request deletion of your personal information</li>
              <li>The right to object to processing of your personal information</li>
              <li>The right to data portability</li>
            </ul>
            <p className="text-white/70 mt-4">
              To exercise these rights, please contact us at privacy@feednatic.com.
            </p>
          </section>

          <section className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-4 text-white">6. Data Security</h2>
            <p className="text-white/70">
              We implement appropriate technical and organizational measures to protect the personal information that we collect and process. However, no method of transmission over the Internet or electronic storage is 100% secure, so we cannot guarantee its absolute security.
            </p>
          </section>

          <section className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-4 text-white">7. Updates to This Policy</h2>
            <p className="text-white/70">
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review this Privacy Policy periodically for any changes.
            </p>
          </section>

          <section className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-4 text-white">8. Contact Us</h2>
            <p className="text-white/70">
              If you have any questions about this Privacy Policy, please contact us at privacy@feednatic.com.
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
